import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, RotateCcw, Sparkles, Wrench, Flag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import { api, reportCustomLevel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const ERA_STYLE = {
  antiquity:   { tint: "from-amber-900/70 to-stone-950/95",  accent: "text-amber-300",  piece: "from-amber-400 to-orange-600" },
  medieval:    { tint: "from-emerald-900/70 to-stone-950/95", accent: "text-emerald-300", piece: "from-emerald-400 to-stone-700" },
  renaissance: { tint: "from-rose-900/70 to-stone-950/95",   accent: "text-rose-200",    piece: "from-rose-300 to-amber-700" },
  future:      { tint: "from-teal-900/70 to-stone-950/95",   accent: "text-teal-300",    piece: "from-teal-400 to-cyan-700" },
};

export default function PlayCustom() {
  const { t } = useTranslation();
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const { player, loading } = usePlayer();
  const [puzzle, setPuzzle] = useState(null);
  const [placed, setPlaced] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [selected, setSelected] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!shareCode) return;
    api.get(`/custom-levels/${shareCode}`).then((r) => setPuzzle(r.data)).catch(() => setErr(t("play_custom.not_found_title")));
  }, [shareCode, t]);

  useEffect(() => {
    if (loading) return;
    if (!player) navigate("/profile");
  }, [loading, player, navigate]);

  const handlePlace = () => {
    setPlaced(true);
    setSelected(false);
    setTimeout(() => setShowWin(true), 600);
    api.post(`/custom-levels/${shareCode}/played`).catch(() => {});
  };

  const reset = () => { setPlaced(false); setShowWin(false); setSelected(false); };

  const handleReport = async () => {
    const reason = window.prompt(t("play_custom.report_prompt"), "");
    if (reason === null) return;
    try {
      const r = await reportCustomLevel(shareCode, reason, player?.id);
      if (r.hidden) toast.success(t("play_custom.reported_hidden"));
      else toast.success(t("play_custom.reported_ok"));
    } catch (e) { toast.error(t("play_custom.report_error")); }
  };

  if (err) {
    return (
      <div className="min-h-screen hero-radial grain flex items-center justify-center p-6">
        <div className="glass-strong rounded-3xl p-8 max-w-md text-center">
          <h2 className="font-serif text-2xl font-bold">{t("play_custom.not_found_title")}</h2>
          <p className="text-stone-400 mt-2">{t("play_custom.not_found_text")}</p>
          <Link to="/editor" className="inline-block mt-4 text-amber-300 hover:text-amber-200 font-bold">{t("play_custom.back_editor")}</Link>
        </div>
      </div>
    );
  }
  if (!puzzle) return null;

  const A = ERA_STYLE[puzzle.era_a];
  const B = ERA_STYLE[puzzle.era_b];
  const eraALabel = t(`eras.${puzzle.era_a}_short`, { defaultValue: t(`eras.${puzzle.era_a}`) });
  const eraBLabel = t(`eras.${puzzle.era_b}_short`, { defaultValue: t(`eras.${puzzle.era_b}`) });

  return (
    <div className="relative min-h-screen bg-stone-950 grain">
      <header className="relative z-20 flex items-center justify-between px-4 sm:px-8 py-4 glass-strong border-b border-white/10">
        <Link to="/editor" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-editor">
          <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t("play_custom.back_editor_short")}</span>
        </Link>
        <div className="text-center flex-1 px-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold flex items-center justify-center gap-1"><Wrench className="w-3 h-3" /> {t("play_custom.banner")}</div>
          <div className="font-serif text-lg sm:text-xl font-bold text-stone-50">{puzzle.title}</div>
          <div className="text-xs text-stone-400">{t("play_custom.by", { name: puzzle.creator_name || t("play_custom.unknown_creator") })}</div>
        </div>
        <button onClick={reset} className="glass rounded-full p-2 hover:border-amber-400/50" data-testid="btn-reset-custom" title={t("common.retry")}>
          <RotateCcw className="w-4 h-4" />
        </button>
        <button onClick={handleReport} className="glass rounded-full p-2 hover:border-rose-400/50 ml-2" data-testid="btn-report-custom" title={t("play_custom.report")} aria-label={t("play_custom.report_aria")}>
          <Flag className="w-4 h-4 text-rose-300" />
        </button>
        <LanguageSwitcher compact />
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-3 sm:px-6 py-6 space-y-4">
        {puzzle.hint && (
          <div className="glass rounded-2xl p-4 text-sm text-stone-300">
            <span className="font-bold text-amber-300 uppercase text-xs tracking-widest mr-2">{t("play_custom.hint_label")}</span>
            {puzzle.hint}
          </div>
        )}

        <div className={`relative overflow-hidden rounded-3xl border border-white/10 p-5 min-h-[220px]`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${A.tint}`} aria-hidden />
          <div className="relative z-10">
            <div className={`text-[10px] uppercase tracking-[0.25em] font-bold ${A.accent}`}>{eraALabel} · A</div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div
                onClick={() => selected && handlePlace()}
                className={`puzzle-slot rounded-2xl px-3 py-3 border-2 border-dashed ${selected ? "dragging-over cursor-pointer" : "border-white/20"} bg-black/35`}
                data-testid="slot-a"
              >
                <div className="text-[11px] uppercase tracking-widest text-stone-300/70 font-bold">{puzzle.era_a_slot_label}</div>
                <div className="mt-1 font-serif text-lg text-stone-100">{placed ? t("play_custom.activated") : t("play_custom.empty_slot")}</div>
              </div>
              {!placed && (
                <div
                  onClick={() => setSelected((v) => !v)}
                  className={`puzzle-piece no-select rounded-2xl px-3 py-3 shadow-lg bg-gradient-to-br ${A.piece} text-stone-950 font-serif font-bold ${selected ? "ring-4 ring-amber-300 ring-offset-2 ring-offset-stone-950" : ""}`}
                  data-testid="piece-a"
                >
                  <div className="text-xs opacity-80 uppercase tracking-wider font-sans font-medium">{selected ? t("play_custom.selected") : t("play_custom.touch_piece")}</div>
                  <div className="text-lg">{puzzle.era_a_piece_label}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`relative overflow-hidden rounded-3xl border border-white/10 p-5 min-h-[160px]`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${B.tint}`} aria-hidden />
          <div className="relative z-10">
            <div className={`text-[10px] uppercase tracking-[0.25em] font-bold ${B.accent}`}>{eraBLabel} · B</div>
            <div className="mt-3 rounded-2xl px-3 py-4 border border-white/10 bg-black/35">
              <div className="text-[11px] uppercase tracking-widest text-stone-300/70 font-bold">{puzzle.era_b_slot_label}</div>
              <div className="mt-1 font-serif text-2xl text-stone-100">{placed ? t("play_custom.illuminated") : t("play_custom.waiting")}</div>
            </div>
          </div>
        </div>
      </main>

      <AnimatePresence>
        {showWin && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            data-testid="win-modal-custom"
          >
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="glass-strong rounded-3xl p-8 max-w-md w-full text-center">
              <div className="text-6xl mb-3 shard-glow">🌟</div>
              <h3 className="font-serif text-3xl font-black text-stone-50">{t("play_custom.win_title")}</h3>
              <p className="mt-2 text-stone-300">{t("play_custom.win_text", { name: puzzle.creator_name || t("play_custom.unknown_creator") })}</p>
              <div className="mt-6 flex gap-3 justify-center">
                <Button onClick={() => navigate("/editor")} variant="outline" className="border-stone-500/50 bg-transparent text-stone-200 rounded-full px-6">{t("nav.editor")}</Button>
                <Button onClick={() => navigate("/levels")} className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6">
                  <Sparkles className="w-4 h-4 mr-2" /> {t("payment.continue_game")}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
